/**
 * Affichage des dates et heures.
 *
 * ⚠️ Le serveur stocke TOUT en temps universel (UTC) — c'est la bonne façon de
 * conserver un instant. Mais l'afficher tel quel est un piège : à Montréal en
 * été, une demande reçue à 16 h 14 se lisait « 20:14 ». Pire, un prospect arrivé
 * à 21 h le lundi s'affichait au mardi, parce qu'on découpait bêtement les dix
 * premiers caractères de la chaîne ISO.
 *
 * On formate donc dans le fuseau DU NAVIGATEUR, pas dans un fuseau codé en dur :
 * Jeff voit l'heure de Montréal, un couvreur du Texas voit la sienne, et le
 * passage à l'heure d'hiver se fait tout seul.
 */

const LOCALES = { fr: "fr-CA", es: "es-US", en: "en-CA" };

/** Date ISO -> objet Date, ou null si la valeur est absente ou illisible. */
function lire(iso) {
  if (!iso) return null;
  // ⚠️ Une chaîne sans fuseau (« 2026-08-05T20:14:00 ») est interprétée comme
  // LOCALE par le navigateur alors qu'elle vient d'un serveur en UTC. On force
  // le Z quand il manque, sinon le décalage s'applique deux fois.
  const brut = typeof iso === "string" && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso)
    ? `${iso}Z`
    : iso;
  const d = new Date(brut);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** « 5 août 2026 » — dans le fuseau du lecteur. */
export function dateCourte(iso, lang = "fr") {
  const d = lire(iso);
  if (!d) return "—";
  return d.toLocaleDateString(LOCALES[lang] || LOCALES.en,
    { day: "numeric", month: "short", year: "numeric" });
}

/** « 5 août, 16:14 » — le format à privilégier dès que l'heure compte. */
export function dateHeure(iso, lang = "fr") {
  const d = lire(iso);
  if (!d) return "—";
  return d.toLocaleString(LOCALES[lang] || LOCALES.en,
    { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Date et heure complètes, pour les infobulles. */
export function dateComplete(iso, lang = "fr") {
  const d = lire(iso);
  if (!d) return "";
  return d.toLocaleString(LOCALES[lang] || LOCALES.en, { dateStyle: "full", timeStyle: "short" });
}

/**
 * « à l'instant », « il y a 2 h », « hier », « il y a 3 j ».
 *
 * Sur une liste de prospects, c'est l'information utile : un couvreur veut
 * savoir ce qui est CHAUD, pas quel jour du calendrier on était.
 */
export function depuis(iso, lang = "fr") {
  const d = lire(iso);
  if (!d) return "—";
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  const fr = lang === "fr";
  const es = lang === "es";
  if (min < 1) return fr ? "à l'instant" : es ? "ahora mismo" : "just now";
  if (min < 60) return fr ? `il y a ${min} min` : es ? `hace ${min} min` : `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return fr ? `il y a ${h} h` : es ? `hace ${h} h` : `${h} h ago`;
  const j = Math.floor(h / 24);
  if (j === 1) return fr ? "hier" : es ? "ayer" : "yesterday";
  if (j < 30) return fr ? `il y a ${j} j` : es ? `hace ${j} d` : `${j} d ago`;
  return dateCourte(iso, lang);
}
